import React, { useEffect, useState } from "react";
import {
    X, ExternalLink, Sparkles, Loader2, Mail, Copy, Star, ShieldCheck, Globe2, MapPin, Briefcase, Send, RotateCcw, Video, Linkedin, Phone, FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RequestScreeningButton } from "@/components/RequestScreeningButton";
import api, { formatApiError, backendUrl } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

/**
 * Slide-in drawer with the full candidate profile + AI recommendations panel.
 * Two tabs: Profile / AI Recommendations.
 */
export default function CandidateDrawer({ candidate: c, searchId, onClose }) {
    const [tab, setTab] = useState("profile");
    const [ai, setAi] = useState(c?.ai_recommendation || null);
    const [loading, setLoading] = useState(false);
    const [inviting, setInviting] = useState(false);
    const [emailOverride, setEmailOverride] = useState("");

    // Compact reappearance-context label — mirrors CandidateCard so a
    // recruiter opening the drawer for a talent who just restored their
    // passport sees the same "Restored just now / 3h ago" pill.
    const restoredLabel = (() => {
        const iso = c?.restored_at;
        if (!iso) return null;
        const t = Date.parse(iso);
        if (isNaN(t)) return null;
        const mins = Math.floor((Date.now() - t) / 60000);
        if (mins < 0 || mins >= 60 * 24 * 7) return null;
        if (mins < 60) return "Restored just now";
        const hours = Math.floor(mins / 60);
        if (hours < 24) return `Restored ${hours}h ago`;
        const days = Math.floor(hours / 24);
        return `Restored ${days}d ago`;
    })();

    // Video intro — fetch a short-lived playback token when the drawer
    // opens for a Simonara candidate. 404 is expected (most talents haven't
    // recorded yet), so we just quietly no-op in that case.
    const [videoIntro, setVideoIntro] = useState(null);
    useEffect(() => {
        if (!c?.email || c?.source !== "simonara") { setVideoIntro(null); return; }
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get(
                    `/admin/recruit/candidates/${encodeURIComponent(c.email)}/video-intro-token`,
                );
                if (alive) setVideoIntro(data);
            } catch {
                if (alive) setVideoIntro(null);
            }
        })();
        return () => { alive = false; };
    }, [c?.email, c?.source]);
    const videoSrc = videoIntro?.playback_token
        ? `${process.env.REACT_APP_BACKEND_URL}/api/talent/video-intro/file/${videoIntro.playback_token}`
        : null;

    const invite = async () => {
        setInviting(true);
        try {
            await api.post("/admin/recruit/invitations/send", {
                search_id: searchId,
                candidate_id: c.candidate_id,
                email_override: (!c.email && emailOverride.trim()) ? emailOverride.trim() : null,
            });
            toast.success("Invitation sent — tracked in Talent CRM.");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setInviting(false); }
    };

    const runAI = async () => {
        setLoading(true);
        try {
            const { data } = await api.post(
                `/admin/recruit/searches/${searchId}/candidates/${encodeURIComponent(c.candidate_id)}/ai-recommend`,
                {},
                { timeout: 90_000 },
            );
            setAi(data);
            setTab("ai");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const copyOutreach = () => {
        if (!ai?.outreach_message) return;
        navigator.clipboard.writeText(ai.outreach_message);
        toast.success("Outreach message copied.");
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex justify-end"
            onClick={onClose}
            data-testid="candidate-drawer"
        >
            <div
                className="w-full max-w-2xl bg-background h-full overflow-y-auto shadow-2xl border-l border-border"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-5 border-b border-border sticky top-0 bg-background z-10">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                {c.source === "simonara" ? (
                                    <Badge variant="outline" className="border-emerald-500/40 text-emerald-700 dark:text-emerald-400 text-[10px]"><ShieldCheck size={9} className="mr-1" /> Simonara Passport</Badge>
                                ) : (
                                    <Badge variant="outline" className="border-sky-500/40 text-sky-700 dark:text-sky-400 text-[10px]"><Globe2 size={9} className="mr-1" /> Apollo · External</Badge>
                                )}
                                {c.has_passport && c.source === "apollo" && (
                                    <Badge variant="outline" className="border-emerald-500/40 text-emerald-700 text-[10px]"><Star size={9} className="mr-1" /> Also in Simonara</Badge>
                                )}
                                {restoredLabel && (
                                    <Badge
                                        data-testid="drawer-restored-badge"
                                        className="bg-amber-500/15 text-amber-800 dark:text-amber-300 border border-amber-500/40 hover:bg-amber-500/15 text-[10px]"
                                        title={`Talent restored their passport at ${new Date(c.restored_at).toLocaleString()}`}
                                    >
                                        <RotateCcw size={9} className="mr-1" /> {restoredLabel}
                                    </Badge>
                                )}
                            </div>
                            <h2 className="font-heading font-medium text-2xl leading-tight truncate">{c.name || "Unknown"}</h2>
                            <p className="text-sm text-muted-foreground truncate">
                                {c.title || "—"}{c.current_company ? ` @ ${c.current_company}` : ""}
                            </p>
                        </div>
                        <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1" data-testid="drawer-close" aria-label="Close">
                            <X size={20} />
                        </button>
                    </div>

                    <div className="mt-4 grid grid-cols-3 gap-3">
                        <ScoreTile label="Match" value={c.match_score} tone={c.match_score >= 70 ? "emerald-600" : c.match_score >= 40 ? "amber-600" : "slate-400"} />
                        <ScoreTile label="Verified" value={c.trust_score || 0} tone="foreground" />
                        <ScoreTile label="Experience" value={c.years_experience || "—"} suffix="yrs" tone="foreground" />
                    </div>

                    {/* Tabs */}
                    <div className="mt-4 inline-flex bg-muted/40 rounded-lg p-1 border border-border">
                        <button
                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${tab === "profile" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground"}`}
                            onClick={() => setTab("profile")}
                            data-testid="drawer-tab-profile"
                        >Profile</button>
                        <button
                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${tab === "ai" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground"}`}
                            onClick={() => setTab("ai")}
                            data-testid="drawer-tab-ai"
                        >AI Recommendations</button>
                    </div>
                </div>

                {/* Body */}
                <div className="p-5 space-y-5">
                    {tab === "profile" && (
                        <>
                            {videoSrc && (
                                <div data-testid="drawer-video-intro">
                                    <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-2 inline-flex items-center gap-1.5">
                                        <Video size={11} /> 60-second intro
                                    </div>
                                    <video
                                        src={videoSrc}
                                        controls
                                        playsInline
                                        className="w-full rounded-lg bg-black"
                                        data-testid="drawer-video-intro-player"
                                    />
                                </div>
                            )}
                            <ProfileRow Icon={MapPin} label="Location" value={c.location} />
                            <ProfileRow Icon={Briefcase} label="Availability" value={c.availability} />
                            <ProfileRow Icon={Mail} label="Email" value={c.email} />
                            {c.linkedin_url && (
                                <div>
                                    <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">LinkedIn</div>
                                    <a href={c.linkedin_url} target="_blank" rel="noreferrer" className="text-sm text-violet-600 hover:underline inline-flex items-center gap-1" data-testid="drawer-linkedin">
                                        {c.linkedin_url} <ExternalLink size={11} />
                                    </a>
                                </div>
                            )}
                            {c.phone && (
                                <div>
                                    <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">Phone</div>
                                    <div className="text-sm text-foreground" data-testid="drawer-phone">{c.phone}</div>
                                </div>
                            )}

                            {/* Multi-channel outreach — Email / LinkedIn / SMS + Resume for internal */}
                            <div>
                                <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">Reach out via</div>
                                <div className="flex flex-wrap gap-2" data-testid="drawer-channel-actions">
                                    {c.email && (
                                        <a
                                            href={`mailto:${c.email}?subject=${encodeURIComponent("Opportunity via Simonara")}`}
                                            className="text-xs px-3 py-1.5 rounded-full border border-slate-500/30 bg-slate-500/10 text-slate-700 hover:bg-slate-500/20 inline-flex items-center gap-1.5 transition"
                                            data-testid="drawer-channel-email"
                                        >
                                            <Mail size={11} /> Email
                                        </a>
                                    )}
                                    {c.linkedin_url && (
                                        <a
                                            href={c.linkedin_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-xs px-3 py-1.5 rounded-full border border-sky-500/30 bg-sky-500/10 text-sky-700 hover:bg-sky-500/20 inline-flex items-center gap-1.5 transition"
                                            data-testid="drawer-channel-linkedin"
                                            title="Open on LinkedIn — hit Message from their profile"
                                        >
                                            <Linkedin size={11} /> LinkedIn message
                                        </a>
                                    )}
                                    {c.phone && (
                                        <a
                                            href={`sms:${c.phone}?body=${encodeURIComponent("Hi — Simonara here. We think you'd be a strong fit for a role we're sourcing. Are you open to a quick chat this week?")}`}
                                            className="text-xs px-3 py-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-700 hover:bg-amber-500/20 inline-flex items-center gap-1.5 transition"
                                            data-testid="drawer-channel-sms"
                                        >
                                            <Phone size={11} /> Text
                                        </a>
                                    )}
                                    {c.source === "simonara" && c.resume_id && (
                                        <button
                                            onClick={() => {
                                                const token = localStorage.getItem("simonara_admin_token");
                                                fetch(backendUrl(`/api/admin/resumes/${c.resume_id}/download`),
                                                    { headers: { Authorization: `Bearer ${token}` } })
                                                    .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob(); })
                                                    .then((blob) => {
                                                        const objUrl = URL.createObjectURL(blob);
                                                        window.open(objUrl, "_blank", "noopener,noreferrer");
                                                        setTimeout(() => URL.revokeObjectURL(objUrl), 60_000);
                                                    })
                                                    .catch(() => toast.error("Couldn't open the resume file."));
                                            }}
                                            className="text-xs px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/20 inline-flex items-center gap-1.5 transition"
                                            data-testid="drawer-view-resume"
                                            title={c.resume_filename || "View resume PDF"}
                                        >
                                            <FileText size={11} /> View resume
                                        </button>
                                    )}
                                </div>
                                {c.source === "apollo" && !c.email && !c.phone && (
                                    <p className="text-[11px] text-muted-foreground mt-2 italic">
                                        Apollo doesn't expose contact info on the free tier for this profile. The LinkedIn link above is your best channel.
                                    </p>
                                )}
                            </div>
                            {c.skills?.length > 0 && (
                                <div>
                                    <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">Skills</div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {c.skills.map((s, i) => (
                                            <span key={i} className={`text-[11px] rounded-md px-2 py-0.5 ${c.matched_skills?.includes(s) ? "bg-violet-500/15 text-violet-700 dark:text-violet-300 font-medium" : "bg-muted"}`}>{s}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {c.employment_history?.length > 0 && (
                                <div>
                                    <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">Employment history</div>
                                    <div className="space-y-2">
                                        {c.employment_history.map((h, i) => (
                                            <div key={i} className="text-sm border-l-2 border-border pl-3">
                                                <div className="font-medium">{h.title || "—"}</div>
                                                <div className="text-xs text-muted-foreground">{h.company || "—"} · {h.start || ""}{h.end ? ` – ${h.end}` : " – Present"}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {c.ai_summary && (
                                <div>
                                    <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">Passport summary</div>
                                    <p className="text-sm text-foreground leading-relaxed">{c.ai_summary}</p>
                                </div>
                            )}

                            <div className="pt-4 border-t border-border space-y-2">
                                {!c.email && c.source === "apollo" && (
                                    <input
                                        type="email"
                                        placeholder="Paste candidate email to invite…"
                                        value={emailOverride}
                                        onChange={(e) => setEmailOverride(e.target.value)}
                                        data-testid="drawer-email-override"
                                        className="w-full text-sm bg-background border border-border rounded-md px-3 py-1.5"
                                    />
                                )}
                                <div className="flex gap-2 flex-wrap">
                                    <Button
                                        onClick={invite}
                                        disabled={inviting || (!c.email && !emailOverride.trim())}
                                        data-testid="drawer-invite-to-simonara"
                                    >
                                        {inviting ? <Loader2 size={13} className="animate-spin mr-2" /> : <Send size={13} className="mr-2" />}
                                        Invite to Simonara
                                    </Button>
                                    <Button variant="outline" onClick={() => setTab("ai")} data-testid="drawer-open-ai">
                                        <Sparkles size={13} className="mr-2" /> AI Recommendations
                                    </Button>
                                    {(c.email || emailOverride.trim()) && (
                                        <RequestScreeningButton
                                            candidateEmail={c.email || emailOverride.trim()}
                                            jobId={c.job_id || null}
                                            context="admin"
                                        />
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {tab === "ai" && (
                        <div data-testid="drawer-ai-panel">
                            {!ai && !loading && (
                                <div className="card-soft p-6 text-center">
                                    <Sparkles size={22} className="mx-auto text-violet-500" />
                                    <div className="font-heading font-medium mt-2">Generate AI recommendation</div>
                                    <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
                                        Claude analyzes this candidate against the AI Hiring Brief™ and drafts fit reasons, concerns, interview questions, and an outreach opener.
                                    </p>
                                    <Button onClick={runAI} className="mt-4" data-testid="drawer-run-ai"><Sparkles size={13} className="mr-2" /> Generate</Button>
                                </div>
                            )}
                            {loading && (
                                <div className="py-10 text-center">
                                    <Loader2 size={22} className="mx-auto animate-spin text-violet-500" />
                                    <p className="text-sm text-muted-foreground mt-2">Analyzing candidate…</p>
                                </div>
                            )}
                            {ai && (
                                <div className="space-y-4">
                                    <AIBlock label="Why they fit" items={ai.why_they_fit} tone="emerald" />
                                    <AIBlock label="Potential concerns" items={ai.potential_concerns} tone="amber" />
                                    <AIBlock label="Suggested interview questions" items={ai.suggested_interview_questions} tone="violet" ordered />
                                    <div>
                                        <div className="flex items-center justify-between mb-1">
                                            <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Outreach opener</div>
                                            <Button size="sm" variant="ghost" onClick={copyOutreach} data-testid="drawer-copy-outreach"><Copy size={11} className="mr-1" /> Copy</Button>
                                        </div>
                                        <div className="rounded-lg bg-muted/30 border border-border p-3 text-sm leading-relaxed whitespace-pre-wrap">
                                            {ai.outreach_message}
                                        </div>
                                    </div>
                                    <AIBlock label="Alternative positions they may fit" items={ai.alternative_positions} tone="sky" />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const TONE_CLASS = {
    "emerald-600": "text-emerald-600",
    "amber-600": "text-amber-600",
    "slate-400": "text-slate-400",
    foreground: "text-foreground",
};

const ScoreTile = ({ label, value, suffix, tone }) => (
    <div className="rounded-md bg-muted/40 border border-border p-2 text-center">
        <div className={`font-heading font-bold text-xl mono-score ${TONE_CLASS[tone] || "text-foreground"}`}>{value}{suffix && <span className="text-xs text-muted-foreground ml-0.5">{suffix}</span>}</div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
);

const ProfileRow = ({ Icon, label, value }) => (
    <div className="flex items-start gap-3 text-sm">
        <Icon size={13} className="text-muted-foreground mt-1 shrink-0" />
        <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</div>
            <div className="truncate">{value || "—"}</div>
        </div>
    </div>
);

const AIBlock = ({ label, items, tone, ordered }) => {
    if (!items || items.length === 0) return null;
    const ListTag = ordered ? "ol" : "ul";
    const listClass = ordered ? "list-decimal" : "list-disc";
    const dotColor = { emerald: "text-emerald-600", amber: "text-amber-600", violet: "text-violet-600", sky: "text-sky-600" }[tone] || "text-muted-foreground";
    return (
        <div>
            <div className={`text-[11px] uppercase tracking-wider font-semibold mb-1 ${dotColor}`}>{label}</div>
            <ListTag className={`${listClass} list-inside space-y-1 text-sm`}>
                {items.map((x, i) => <li key={i}>{x}</li>)}
            </ListTag>
        </div>
    );
};
